import React, { useContext, useState } from "react";
import { observer } from "mobx-react";
import { KeyRound, Trash2 } from "lucide-react";
import { AppWorkspaceContext } from "../../stores/AppWorkspace";
import { ApiToken } from "../../stores/ApiToken";
import { Badge, Button, CopyButton, Field, Input, PageHeader, Panel } from "../../../ui";

const selectAll = (e: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>) => {
  e.currentTarget.select();
};

const LscUriField: React.FC<{ token: ApiToken }> = ({ token }) => {
  const uri = token.lscUri;
  return (
    <Field
      label="Lightning Swap Connect URI"
      hint="Store the whole URI as one secret — it carries both the key and the secret. Set it as LSC_URI_PRIMARY (or LSC_URI_BACKUP) on your server."
      className="mt-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
        <Input
          readOnly
          value={uri}
          onFocus={selectAll}
          onClick={selectAll}
          className="min-w-0 flex-1 cursor-pointer font-mono text-xs"
        />
        <CopyButton
          value={uri}
          label="Copy"
          size="md"
          variant="accent"
          className="shrink-0 sm:self-stretch sm:px-6"
        />
      </div>
    </Field>
  );
};

const ApiTokensPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext);
  const [label, setLabel] = useState("");

  const renderActions = (token: ApiToken) => {
    if (!token.isActive) {
      return <Badge tone="muted">Revoked</Badge>;
    }
    return (
      <Button
        size="xs"
        variant="danger"
        leftIcon={<Trash2 size={14} />}
        onClick={() => workspace.revokeApiToken(token)}
        loading={workspace.areWeWaitingOnRails}
      >
        Revoke
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="API keys"
        subtitle={
          <>
            HMAC auth for <code className="rounded bg-border/50 px-1">/api/v2</code> — X-API-KEY +
            X-API-SIGN.
          </>
        }
      />

      {workspace.apiUsage && (
        <Panel className="!p-4">
          <p className="text-sm">
            Rate limit:{" "}
            <strong>
              {workspace.apiUsage.data.used_weight} / {workspace.apiUsage.data.weight_limit}
            </strong>{" "}
            weight this minute
            {workspace.apiUsage.data.create_blocked ? " (create blocked)" : ""}.{" "}
            {workspace.apiUsage.data.remaining} remaining.
          </p>
        </Panel>
      )}

      <Panel>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field label="Label" className="min-w-0 flex-1">
            <Input
              placeholder="production-bot"
              value={label}
              onChange={(e) => setLabel(e.currentTarget.value)}
            />
          </Field>
          <Button
            leftIcon={<KeyRound size={16} />}
            onClick={() => workspace.createApiToken(label)}
            loading={workspace.areWeWaitingOnRails}
          >
            Create key
          </Button>
        </div>
      </Panel>

      {workspace.apiTokens.length === 0 ? (
        <Panel className="!py-8 text-center text-muted">No API keys yet.</Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {workspace.apiTokens.map((t) => (
            <Panel key={t.uuid}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-ink">{t.label}</h3>
                    {t.isActive ? (
                      <Badge tone="success">Active</Badge>
                    ) : (
                      <Badge tone="muted">Revoked</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted">
                    Last used:{" "}
                    {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : "—"}
                  </p>
                </div>
                {renderActions(t)}
              </div>
              <LscUriField token={t} />
            </Panel>
          ))}
        </div>
      )}

      <p className="text-sm text-muted">
        See <a href="/api_docs">API documentation</a> for curl examples.
      </p>
    </div>
  );
});

export default ApiTokensPage;
